import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2 } from "lucide-react";
import { DECK_THEME as T } from "../lib/deckModel";
import { DeckShell } from "../components/decks/DeckShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/decks`;

export function EmbeddedDeck({ deck, preview }) {
  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: T.navy }} data-testid="deck-embedded">
      {preview && <div className="text-center text-[11px] uppercase tracking-[0.2em] py-1.5 text-white" style={{ background: T.accent }} data-testid="deck-preview-banner">Preview — visitors see this page at arqitek.co/{deck.slug}</div>}
      <iframe src={deck.embed_url} title={deck.name} className="flex-1 w-full border-0" allow="fullscreen; autoplay; clipboard-write" allowFullScreen data-testid="deck-embedded-frame" />
    </div>
  );
}

export function DeckViewer({ deck, preview }) {
  return deck.embed_url ? <EmbeddedDeck deck={deck} preview={preview} /> : <NativeDeck deck={deck} preview={preview} />;
}

function NativeDeck({ deck, preview }) {
  return <DeckShell deck={deck} preview={preview} />;
}

export default function PublicDeck() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });
  const [pw, setPw] = useState("");
  const load = (password) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    axios.get(`${API}/${slug}`, { headers: password ? { "X-Deck-Password": password } : {} })
      .then((r) => { if (password) sessionStorage.setItem(`deckpw:${slug}`, password); setState({ deck: r.data }); })
      .catch((e) => {
        if (e.response?.status === 401) setState({ locked: true, name: e.response.data?.name, error: password ? "Incorrect password" : null });
        else setState({ notFound: true });
      });
  };
  useEffect(() => { load(sessionStorage.getItem(`deckpw:${slug}`) || ""); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.notFound) return <Navigate to="/" replace />;
  if (state.deck) return <DeckViewer deck={state.deck} />;
  if (state.locked) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: T.navy }} data-testid="deck-password-gate">
      <form onSubmit={(e) => { e.preventDefault(); load(pw); }} className="w-full max-w-sm text-center">
        <img src="/logo.png" alt="ARQITEK" className="h-5 mx-auto mb-10" />
        <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-5" style={{ background: "rgba(255,255,255,0.08)" }}><Lock className="h-5 w-5 text-white" /></div>
        <h1 className="text-3xl text-white" style={{ fontFamily: T.serif }}>{state.name || "Private project deck"}</h1>
        <p className="mt-2 text-sm text-white/60">This presentation is password protected.</p>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Enter password" autoFocus className="mt-8 w-full rounded-full bg-white/10 border border-white/15 px-5 py-3 text-white placeholder-white/40 outline-none focus:border-white/40" data-testid="deck-password-input" />
        {state.error && <p className="mt-3 text-sm text-red-300" data-testid="deck-password-error">{state.error}</p>}
        <button type="submit" className="mt-4 w-full rounded-full py-3 text-sm font-semibold text-white hover:opacity-90 transition-opacity" style={{ background: T.accent }} data-testid="deck-password-submit">View deck</button>
      </form>
    </div>
  );
  return <div className="min-h-screen flex items-center justify-center" style={{ background: T.navy }}><Loader2 className="h-6 w-6 animate-spin text-white" /></div>;
}
